<template>
  <div v-if="canManageCodes" class="mt-5 pt-4 border-t border-surface" data-testid="student-linking-code">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="min-w-0">
        <p class="text-xs uppercase text-muted-color font-semibold mb-1">Parent portal linking code</p>
        <div v-if="linkingCode" class="flex items-center gap-2">
          <span class="font-mono text-lg font-bold text-primary tracking-widest">{{ linkingCode }}</span>
          <Button v-tooltip="'Copy code'" icon="pi pi-copy" text rounded size="small" aria-label="Copy code" @click="copyCode" />
        </div>
        <p v-else class="text-sm text-muted-color">No linking code yet.</p>
      </div>
      <Button
        :label="linkingCode ? 'Regenerate' : 'Generate code'"
        :icon="linkingCode ? 'pi pi-refresh' : 'pi pi-key'"
        size="small"
        outlined
        :loading="generatingFor === studentId"
        data-testid="btn-generate-linking-code"
        @click="onGenerate"
      />
    </div>
    <p class="text-xs text-muted-color mt-2">
      Parents use this code to register on the parent portal, or to add this student to their account.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { useStudentLinkingCodes } from '@/composables/mutations/guardians/useStudentLinkingCodes';

const props = defineProps<{ studentId: string; linkingCode?: string | null }>();
const emit = defineEmits<{ (e: 'refresh'): void }>();

const toast = useToast();
const confirm = useConfirm();
const userStore = useUserStore();
const { generateFor, generatingFor } = useStudentLinkingCodes();

// The backend returns the code (and lets it be changed) only for guardians.create;
// anyone else would see "No linking code yet" for every student.
const canManageCodes = computed(() => can(userStore.currentUser, AppPermissions.Guardians.Actions.Create, AppPermissions.Guardians.Resource));

async function generate() {
  if (await generateFor(props.studentId)) emit('refresh');
}

function onGenerate() {
  if (!props.linkingCode) {
    void generate();
    return;
  }
  // A new code stops the old one working for parents who have not used it yet.
  confirm.require({
    header: 'Replace the linking code?',
    message: 'Parents who were given the current code will not be able to use it any more.',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Replace code',
    rejectLabel: 'Cancel',
    accept: () => generate(),
  });
}

async function copyCode() {
  if (!props.linkingCode) return;
  try {
    await navigator.clipboard.writeText(props.linkingCode);
    toast.add({ severity: 'info', summary: 'Copied', detail: 'Linking code copied.', life: 2000 });
  } catch {
    toast.add({ severity: 'warn', summary: 'Not copied', detail: 'Copy the code by hand.', life: 2000 });
  }
}
</script>
